import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { saveLoginReferrer } from '@/utils/loginReferrer';
import ChatsWindow from 'components/ChatsWindow';
import { useChatSidebar } from 'context/ChatSidebarContext';
import { FREIGHTS, TRANSPORT } from 'helpers/routes';
import styles from './styles.module.scss';

const logoSrc = new URL('../../img/logo.svg', import.meta.url).href;
const profileIcon = new URL('../../img/main-page/profile.svg', import.meta.url).href;
const chatIcon = new URL('../../img/main-page/chat.svg', import.meta.url).href;
const menuIcon = new URL('../../img/main-page/menu.svg', import.meta.url).href;
const cubeIcon = new URL('../../img/main-page/cube.svg', import.meta.url).href;
const truckIcon = new URL('../../img/main-page/truck.svg', import.meta.url).href;

function Header() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const authenticated = useSelector((state) => state.auth.authenticated);
  const profileData = useSelector((state) => state.profile.profileData);
  const hasNewMessage = useSelector((state) => state.newMessage.hasNewMessage);
  const unreadCount = useSelector((state) => state.newMessage.unreadCount);
  const { toggleSidebar } = useChatSidebar();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);

  const isMainPage = pathname === '/';
  const isCargoSearch = pathname === FREIGHTS.search;
  const isTransportSearch = pathname === TRANSPORT.search;
  const showSearchTabs = !isMainPage || scrolled;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMainPage) {
      setScrolled(false);
      return undefined;
    }
    const scrollContainer = document.querySelector('.App');
    if (!scrollContainer) return undefined;
    const handleScroll = () => setScrolled(scrollContainer.scrollTop > 50);
    handleScroll();
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isMainPage]);

  const primaryNavItems = [
    { to: '/freight/search', label: t('cargo.heading'), icon: cubeIcon },
    { to: '/transport/search', label: t('header.truckTransportation'), icon: truckIcon },
  ];

  const secondaryNavItems = [
    { to: '/news', label: t('header.news') },
    { to: '/forums', label: t('header.submenu.forums') },
    { to: '/partners', label: t('header.partners') },
    { to: '/useful', label: t('header.useful') },
    { to: '/tutorials', label: t('header.tutorials') },
    { to: '/contacts', label: t('header.contacts') },
  ];

  const handleProfileClick = () => {
    if (!authenticated) {
      saveLoginReferrer();
    }
  };

  const profileLink = authenticated ? '/profile/company' : '/auth/login';

  return (
    <header className={styles.header} data-id="main-header">
      <div className={styles.content}>
        <div className={styles.left}>
          <Link to="/" className={styles.logo}>
            <img src={logoSrc} alt="Blago" />
          </Link>

          {showSearchTabs && (
            <div className={styles.tabs}>
              <Link
                to={FREIGHTS.search}
                className={cn(styles.tab, { [styles.tabActive]: isCargoSearch })}
              >
                {t('header.findCargo', 'Найти грузы')}
              </Link>
              <Link
                to={TRANSPORT.search}
                className={cn(styles.tab, { [styles.tabActive]: isTransportSearch })}
              >
                {t('header.findTransport', 'Найти транспорт')}
              </Link>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {authenticated && isCargoSearch && (
            <Link to={FREIGHTS.add} className={styles.addButton}>
              {t('header.addCargo', 'Добавить груз')}
            </Link>
          )}
          {authenticated && isTransportSearch && (
            <Link to={TRANSPORT.addToExchange} className={styles.addButton}>
              {t('header.addTransport', 'Добавить транспорт')}
            </Link>
          )}

          <Link
            to={profileLink}
            className={styles.actionButton}
            onClick={handleProfileClick}
            state={!authenticated ? { modal: true } : undefined}
          >
            {authenticated && profileData?.picture ? (
              <span
                className={styles.profileAvatar}
                style={{ backgroundImage: `url("${profileData.picture}")` }}
              />
            ) : (
              <img src={profileIcon} alt="" className={styles.actionIcon} />
            )}
            <span className={styles.actionLabel}>{t('header.profile', 'Профиль')}</span>
          </Link>

          {authenticated && (
            <>
              <ChatsWindow />
              <button type="button" className={styles.actionButton} onClick={toggleSidebar}>
                <span className={styles.chatIconWrapper}>
                  <img src={chatIcon} alt="" className={styles.actionIcon} />
                  {hasNewMessage && unreadCount > 0 && (
                    <span className={styles.chatBadge}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
                <span className={styles.actionLabel}>{t('header.chats', 'Чаты')}</span>
              </button>
            </>
          )}

          <div className={styles.menuWrapper} ref={menuRef}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <img src={menuIcon} alt="" className={styles.actionIcon} />
              <span className={styles.actionLabel}>{t('header.menu', 'Меню')}</span>
            </button>

            {menuOpen && (
              <div className={styles.dropdown}>
                <nav className={styles.dropdownGroup}>
                  {primaryNavItems.map(({ to, label, icon }) => (
                    <Link
                      key={to}
                      to={to}
                      className={cn(styles.dropdownItem, {
                        [styles.dropdownItemActive]: pathname === to,
                      })}
                    >
                      <img src={icon} alt="" className={styles.dropdownItemIcon} />
                      <span>{label}</span>
                    </Link>
                  ))}
                </nav>
                <div className={styles.dropdownDivider} />
                <nav className={styles.dropdownGroup}>
                  {secondaryNavItems.map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className={cn(styles.dropdownItem, {
                        [styles.dropdownItemActive]: pathname === to,
                      })}
                    >
                      <span>{label}</span>
                    </Link>
                  ))}
                </nav>
                {!authenticated && (
                  <>
                    <div className={styles.dropdownDivider} />
                    <Link
                      to="/auth/login"
                      state={{ modal: true }}
                      className={styles.dropdownItem}
                      onClick={() => saveLoginReferrer()}
                    >
                      {t('header.entrance')}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
